// SearchField and SearchButton: search is one of the few pills (design-standard "Shape"). The
// kit well drawn round, a magnifier that lights in the accent while a query is narrowing what
// shows, and a clear key once there is text. Escape clears too. SearchButton is the same pill as
// a button, for a search that opens somewhere else (the nav's command palette).
// docs/operations/design-standard.md, "Controls".

import React, { useRef } from 'react';
import Appear from './Appear';
import { fieldClass } from './Field';
import { IconClose, IconSearch } from './icons';
import { CONTROL_MOTION, FOCUS, PRESS, STATE, TOUCH } from './motion';
import Tooltip from './Tooltip';

export type SearchSize = 'sm' | 'md' | 'lg';

const ICON: Record<SearchSize, string> = { sm: 'left-2.5 h-3.5 w-3.5', md: 'left-3 h-4 w-4', lg: 'left-3.5 h-5 w-5' };
const PAD_LEFT: Record<SearchSize, string> = { sm: 'pl-7', md: 'pl-9', lg: 'pl-11' };
const CLEAR: Record<SearchSize, string> = { sm: 'h-5 w-5', md: 'h-6 w-6', lg: 'h-8 w-8' };

type NativeInput = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'size' | 'type' | 'className' | 'aria-label'>;

export interface SearchFieldProps extends NativeInput {
  value: string;
  onChange: (next: string) => void;
  /**
   * The accessible name, and not the placeholder: a placeholder disappears the moment somebody
   * types, which is when a reader needs the name. Say what the search reaches.
   */
  label: string;
  /** `sm` 28px in a toolbar or the nav, `md` 32px, `lg` 44px on shop-floor screens. */
  size?: SearchSize;
  /** A search is running: the magnifier breathes and the field reports busy. */
  busy?: boolean;
  /** 40px under a coarse pointer, for forms filled on a phone at the bench. */
  thumb?: boolean;
  /** Codes, serials, scans: the mono face, aligned figures. */
  mono?: boolean;
  /** An action key inside the right edge, after the clear key: a camera that scans into the field. */
  trailing?: React.ReactNode;
  /** Width and layout, on the box that holds the field. */
  className?: string;
}

export const SearchField = React.forwardRef<HTMLInputElement, SearchFieldProps>(({
  value,
  onChange,
  label,
  size = 'md',
  busy,
  thumb,
  mono,
  trailing,
  className = '',
  placeholder = 'Search',
  disabled,
  onKeyDown,
  ...rest
}, ref) => {
  const own = useRef<HTMLInputElement | null>(null);
  const setRef = (el: HTMLInputElement | null) => {
    own.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = el;
  };
  const filled = value.length > 0;
  const clearable = filled && !disabled;
  const right = trailing ? 'pr-16' : clearable ? (size === 'lg' ? 'pr-11' : 'pr-8') : size === 'lg' ? 'pr-4' : 'pr-3';

  return (
    <div className={`relative min-w-0 ${className}`}>
      <IconSearch
        className={`pointer-events-none absolute top-1/2 -translate-y-1/2 transition-colors duration-quick ease-emphasized ${ICON[size]} ${
          filled && !disabled ? 'text-primary' : 'text-on-surface-variant'
        } ${busy ? 'animate-pulse motion-reduce:animate-none' : ''}`}
      />
      <input
        ref={setRef}
        type="search"
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        aria-label={label}
        aria-busy={busy || undefined}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          // Cleared and consumed; a caller that also uses Escape reads defaultPrevented.
          if (e.key === 'Escape' && filled) {
            e.preventDefault();
            e.stopPropagation();
            onChange('');
          }
          if (onKeyDown) onKeyDown(e);
        }}
        // A search input draws its own clear cross in Chrome and Safari; two is one too many.
        className={`${fieldClass({ size, shape: 'pill', thumb })} appearance-none [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden ${PAD_LEFT[size]} ${right} ${
          mono ? 'font-mono tabular-nums' : ''
        }`}
        {...rest}
      />
      <span role="status" className="sr-only">{busy ? 'Searching' : ''}</span>
      <span className="absolute inset-y-0 right-1 flex items-center gap-0.5">
        <Appear show={clearable} className="inline-flex">
          <Tooltip label="Clear search">
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => { onChange(''); own.current?.focus(); }}
              className={`inline-flex items-center justify-center rounded-full text-on-surface-variant hover:text-on-surface ${CLEAR[size]} ${STATE} ${CONTROL_MOTION} ${PRESS} ${FOCUS} ${TOUCH}`}
            >
              <IconClose className={size === 'lg' ? 'h-3.5 w-3.5' : 'h-3 w-3'} />
            </button>
          </Tooltip>
        </Appear>
        {trailing}
      </span>
    </div>
  );
});
SearchField.displayName = 'SearchField';

export interface SearchButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'title' | 'aria-label'> {
  /** The accessible name: what pressing it opens ("Search"). */
  label: string;
  size?: 'sm' | 'md';
  /** The visible prompt ("Search..."). Hide it at narrow widths with a class on your own span. */
  children?: React.ReactNode;
}

/** The search pill as a button: it opens a search rather than holding one. */
export const SearchButton = React.forwardRef<HTMLButtonElement, SearchButtonProps>(
  ({ label, size = 'md', className = '', type = 'button', children, ...rest }, ref) => (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      className={`inline-flex min-w-0 select-none items-center gap-2 whitespace-nowrap rounded-full bg-surface-container-high text-on-surface-variant shadow-recess hover:text-on-surface ${
        size === 'sm' ? 'h-7 px-2.5 text-[13px]' : 'h-8 px-3 text-sm'
      } ${STATE} ${CONTROL_MOTION} ${FOCUS} ${TOUCH} ${className}`}
      {...rest}
    >
      <IconSearch className={size === 'sm' ? 'h-3.5 w-3.5 flex-none' : 'h-4 w-4 flex-none'} />
      {children}
    </button>
  ),
);
SearchButton.displayName = 'SearchButton';

export default SearchField;
